import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Alert, Button, Typography } from '@mui/material';

import useCountdown from '../../hooks/useCountdown';

// Intervalo da consulta do estado do token enquanto a entrega está pendente: proposta de 15 s, a confirmar com o produto.
export const TOKEN_STATE_POLL_INTERVAL_MS = 15000;

const PENDING_DELIVERY = ['accepted', 'unconfirmed'];

// Backend sem o GET de estado do token: a rota não existe (404 sem code) ou não aceita GET (405).
const isLegacyBackend = (apiError) => apiError.status === 405 || (apiError.status === 404 && !apiError.code);

// Estado da tela de confirmação. Vindo do botão Assinar (state.requestToken) pede o token; na recarga lê o
// estado do token, sem pedir novo código. requestToken e loadState rejeitam com toApiError.
export function useTokenChallenge(requestToken, loadState) {
  const location = useLocation();
  const navigate = useNavigate();
  const [token, setToken] = useState(null);
  const [error, setError] = useState(null);
  const [resendAt, setResendAt] = useState(0);
  // bloqueio do retry_after (429) fica separado: o resend_in do estado do token só conta o cooldown
  const [blockedUntil, setBlockedUntil] = useState(0);
  const [notPending, setNotPending] = useState(false);

  const receive = (data) => {
    // corpo sem channel = backend antigo, que só envia por e-mail
    setToken({ ...data, channel: data.channel || 'email' });
    setResendAt(Date.now() + (data.resend_in || 0) * 1000);
    if (data.delivery === 'failed_delivery') setError({ message: data.message });
  };

  const fail = (apiError) => {
    setError(apiError);
    if (apiError.retryAfter) setBlockedUntil(Date.now() + apiError.retryAfter * 1000);
  };

  const resend = () => {
    setError(null);
    return requestToken().then(receive, fail);
  };

  useEffect(() => {
    if (location.state?.requestToken) {
      navigate(location.pathname, { replace: true });
      resend();
      return;
    }
    loadState().then(receive, (apiError) => {
      if (isLegacyBackend(apiError)) {
        receive({});
        return;
      }
      setNotPending(apiError.status === 404);
      fail(apiError);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- só na montagem
  }, []);

  useEffect(() => {
    if (!PENDING_DELIVERY.includes(token?.delivery) || !(token.expires_in > 0)) return undefined;

    const expiresAt = Date.now() + token.expires_in * 1000;
    const id = setInterval(() => {
      if (Date.now() >= expiresAt) clearInterval(id);
      else loadState().then(receive, () => {});
    }, TOKEN_STATE_POLL_INTERVAL_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reinicia a cada resposta do token ou do estado
  }, [token]);

  return {
    channel: token?.channel,
    phoneMasked: token?.phone_masked,
    delivery: token?.delivery,
    resendAt: Math.max(resendAt, blockedUntil),
    error,
    onResend: notPending ? undefined : resend,
    onSignError: fail,
  };
}

export default function TokenChallenge({ channel, phoneMasked, resendAt, delivery, error, onResend, children }) {
  const resendIn = useCountdown(resendAt);
  const [resent, setResent] = useState(false);

  const handleResend = () => onResend().then(() => setResent(true));

  return (
    <>
      {channel && (
        <Typography
          variant="body1"
          sx={{ fontSize: { xs: '0.875rem', sm: '1rem', md: '1.125rem' }, textAlign: 'center', px: { xs: 2, sm: 0 } }}
        >
          {channel === 'whatsapp'
            ? `Token enviado para o seu WhatsApp${phoneMasked ? ` ${phoneMasked}` : ''}`
            : 'Token enviado para o seu email'}
        </Typography>
      )}
      {delivery === 'unconfirmed' ? (
        <Alert severity="info">A mensagem pode demorar a chegar. Use o código mais recente.</Alert>
      ) : (
        resent && !error && <Alert severity="success">Novo código enviado. Use o código mais recente.</Alert>
      )}
      {error && <Alert severity="error">{error.message}</Alert>}
      {children}
      {onResend && (
        <Button
          variant={error?.action === 'request_new_code' ? 'contained' : 'outlined'}
          disabled={resendIn > 0}
          onClick={handleResend}
        >
          {resendIn > 0 ? `Enviar novo código (${resendIn} s)` : 'Enviar novo código'}
        </Button>
      )}
    </>
  );
}

TokenChallenge.propTypes = {
  channel: PropTypes.string,
  phoneMasked: PropTypes.string,
  resendAt: PropTypes.number,
  delivery: PropTypes.string,
  error: PropTypes.shape({ message: PropTypes.string, action: PropTypes.string }),
  onResend: PropTypes.func,
  children: PropTypes.node,
};
